import { useLazyQuery } from '@apollo/client';
import {
  IDashboardFromTemplateQuery,
  IDashboardFromTemplateQueryVariables,
} from '../../../utils/apollo/graphqlSchema';
import { dashboardFromTemplateQuery } from '../../../utils/apollo/dashboards';
import { useCallback } from 'react';
import { assertNotNil } from '../../../utils/types';
import useUpdateDashboardStore from './useUpdateDashboardStore';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { dashboardViewed } from '../../../utils/analytics/events/dashboard/events';

/**
 * Whenever an add/edit operations fails we reload the latest version of the dashboard to recover if the error is
 * caused by stale version on the clients and user can continue their edit operation
 *
 * @param baseId
 */
const useDashboardReload = (baseId: string) => {
  const [reloadDashboardQuery] = useLazyQuery<
    IDashboardFromTemplateQuery,
    IDashboardFromTemplateQueryVariables
  >(dashboardFromTemplateQuery, { variables: { baseId } });
  const updateDashboardStore = useUpdateDashboardStore();
  const { track } = useAnalytics();

  return useCallback(
    async (retainAnalyses = false) => {
      const response = await reloadDashboardQuery();
      const dashboard = response.data?.dashboardFromTemplate;
      assertNotNil(dashboard);
      updateDashboardStore({ dashboard, retainAnalyses });
      track(dashboardViewed(dashboard));
      return dashboard;
    },
    [reloadDashboardQuery, track, updateDashboardStore],
  );
};

export default useDashboardReload;
